import { useRef, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useCollection } from "./useCollection";
import { uploadImage } from "./ImageInput";
import { api, errMsg, imgUrl } from "../lib/api";

export default function GalleryAdmin() {
  const { items, setItems, create, save, remove, reload } = useCollection("gallery");
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    setBusy(true);
    const min = items.length ? Math.min(...items.map((x) => x.order)) : 0;
    let n = 0;
    for (const f of Array.from(e.target.files || [])) {
      try {
        const url = await uploadImage(f);
        await create({ image: url, caption: "", order: min - 1 - n++ }, null);
      } catch (err) { toast.error(`${f.name}: ${errMsg(err)}`); }
    }
    if (n) toast.success(`${n} image(s) added to the top of the gallery`);
    setBusy(false);
    e.target.value = "";
  };

  const move = async (i, d) => {
    const a = items[i], b = items[i + d];
    if (!b) return;
    try {
      const strip = ({ id, created_at, ...r }) => r;
      await Promise.all([api.put(`/admin/gallery/${a.id}`, { ...strip(a), order: b.order }), api.put(`/admin/gallery/${b.id}`, { ...strip(b), order: a.order === b.order ? a.order + d : a.order })]);
      reload();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-4xl font-black uppercase">Gallery <span className="text-lg text-slate-500">({items.length})</span></h1>
        <button className="btn-red !py-2" onClick={() => ref.current?.click()} disabled={busy} data-testid="admin-gallery-upload-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Upload Images
        </button>
        <input ref={ref} type="file" multiple accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={upload} data-testid="admin-gallery-upload-input" />
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {items.map((g, i) => (
          <div key={g.id} className="panel overflow-hidden" data-testid={`admin-gallery-item-${i}`}>
            <img src={imgUrl(g.image)} alt={g.caption} className="aspect-square w-full object-cover" loading="lazy" />
            <div className="space-y-2 p-3">
              <input className="field !py-2 text-xs" placeholder="Caption" value={g.caption}
                onChange={(e) => setItems((xs) => xs.map((x) => (x.id === g.id ? { ...x, caption: e.target.value } : x)))}
                onBlur={() => save(g, "Caption saved")} data-testid={`admin-gallery-caption-${i}`} />
              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} className="rounded p-1.5 hover:bg-white/10" data-testid={`admin-gallery-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
                  <button onClick={() => move(i, 1)} className="rounded p-1.5 hover:bg-white/10" data-testid={`admin-gallery-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
                </div>
                <button onClick={() => window.confirm("Remove this image?") && remove(g.id)} className="rounded p-1.5 text-slate-500 hover:text-red-400" data-testid={`admin-gallery-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
